import {
  CLUBS_CITY_SEO,
  getClubsCityEntry,
  getClubsCitySlug,
} from "../config/seo";
import { getEventClub } from "./eventClub";
import { resolveApiCity } from "./city";

const DEFAULT_CITY_SLUG = "mumbai";
const DEFAULT_CITY_LABEL = "Mumbai";

function prettifyCitySlug(slug) {
  return `${slug || ""}`
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function slugifySegment(value) {
  return `${value || ""}`
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Supported list-city label, or null if unknown (e.g. adsasdasd). */
export function resolveSupportedCityLabel(cityHint) {
  if (!cityHint) return null;

  const entry =
    getClubsCityEntry(cityHint) ||
    getClubsCityEntry(resolveApiCity(cityHint));

  return entry?.label || null;
}

/** Supported URL city slug: mumbai, delhi-ncr */
export function resolveSupportedCitySlug(cityHint) {
  const label = resolveSupportedCityLabel(cityHint);
  if (!label) return null;
  return getClubsCitySlug(label) || null;
}

/** Resolve city slug: selected → location → mumbai */
export function resolveDefaultCitySlug(selectedCity, locationCity) {
  return (
    resolveSupportedCitySlug(selectedCity) ||
    resolveSupportedCitySlug(locationCity) ||
    DEFAULT_CITY_SLUG
  );
}

export function getCityLabelFromSlug(citySlugOrName) {
  const supported = resolveSupportedCityLabel(citySlugOrName);
  if (supported) return supported;

  const entry = getClubsCityEntry(citySlugOrName);
  if (entry?.label) return entry.label;

  const slug = getClubsCitySlug(citySlugOrName);
  const fromSlug = slug ? getClubsCityEntry(slug) : null;
  if (fromSlug?.label) return fromSlug.label;

  const raw = `${citySlugOrName ?? ""}`.trim();
  if (raw) return raw;

  return prettifyCitySlug(slug || citySlugOrName) || DEFAULT_CITY_LABEL;
}

export function resolveDefaultCityLabel(selectedCity, locationCity) {
  return (
    resolveSupportedCityLabel(selectedCity) ||
    resolveSupportedCityLabel(locationCity) ||
    DEFAULT_CITY_LABEL
  );
}

/** Public list URLs: /clubs/mumbai, /clubs/delhi-ncr */
export function getClubsListPath(cityHint) {
  const slug =
    resolveSupportedCitySlug(cityHint) || DEFAULT_CITY_SLUG;
  return `/clubs/${slug}`;
}

/** Public list URLs: /events/mumbai, /events/delhi-ncr */
export function getEventsListPath(cityHint) {
  const slug =
    resolveSupportedCitySlug(cityHint) || DEFAULT_CITY_SLUG;
  return `/events/${slug}`;
}

/** Public club detail path — /club/goa/{clubSlug} */
export function getClubPath(club, cityHint) {
  if (!club) return getClubsListPath(cityHint);

  const clubKey = club.slug || club._id || club.id;
  if (!clubKey) return getClubsListPath(cityHint);

  const citySlug =
    getClubsCitySlug(
      resolveApiCity(
        cityHint || club.city || club?.address?.city || DEFAULT_CITY_LABEL,
      ),
    ) || DEFAULT_CITY_SLUG;

  return `/club/${citySlug}/${clubKey}`;
}

/** Public artist detail path — /artist/{slug|id} */
export function getArtistPath(artist) {
  if (!artist) return "/artists";

  const key = artist.slug || artist._id || artist.id;
  if (!key) return "/artists";

  return `/artist/${key}`;
}

/** Prefer list/URL hint, then venue/club city, then event.city (Thane → Mumbai). */
function resolveEventCitySlug(event, cityHint) {
  const club = getEventClub(event);
  const candidates = [
    cityHint,
    club?.city,
    club?.address?.city,
    event?.address?.city,
    event?.city,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const supported = resolveSupportedCityLabel(candidate);
    if (supported) {
      return getClubsCitySlug(supported) || DEFAULT_CITY_SLUG;
    }
  }

  return DEFAULT_CITY_SLUG;
}

/** Public event detail path — /event/mumbai/{eventSlug} */
export function getEventPath(event, cityHint) {
  if (!event) return getEventsListPath(cityHint);

  const eventKey =
    event.slug ||
    slugifySegment(event.title) ||
    event._id ||
    event.id;
  if (!eventKey) return getEventsListPath(cityHint);

  const citySlug = resolveEventCitySlug(event, cityHint);

  return `/event/${citySlug}/${eventKey}`;
}

export { CLUBS_CITY_SEO, DEFAULT_CITY_SLUG, DEFAULT_CITY_LABEL };
